<template>
    <section id="Schedule_Visit_Hos_Pal_Patient" v-if="!isFrequencyWarningRequested && !isScheduling">
        <FormSection
            :full-height="false"
            title="Employee Scheduler"
            :application-id="visitDetails.Application"
            :show-product-pill="true"
        >
            <template #content>
                <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                    <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                    <span class="mt-1 big-text">
                        <b>Loading...</b>
                    </span>
                </section>
                <section class="task-details" v-else>
                    <div class="container row m-0 p-1">
                        <div class="col-12 mt-2">
                            <strong class="me-2">Patient:</strong>
                            <FormatName :first-name="visitDetails.FirstName" :last-name="visitDetails.LastName" />
                            <p class="mt-2 mb-1">
                                <strong class="me-2">{{ carePeriodTitle }}</strong>
                                <span>{{ carePeriod }}</span>
                            </p>
                            <p class="mt-2">
                                <strong class="me-2">Agency:</strong>
                                <span> {{ visitDetails.AgencyName }}</span>
                            </p>
                        </div>
                        <div class="col-12">
                            <div class="form-group form-check custom-checkbox mt-2" v-if="!isPalliativeSolution">
                                <input
                                    type="checkbox"
                                    id="view-visit-orders"
                                    class="form-check-input"
                                    v-model="viewVisitOrders"
                                />
                                <label class="form-check-label" for="view-visit-orders"> View Visit Orders </label>
                            </div>
                        </div>
                        <div class="form-group col-12" v-if="viewVisitOrders">
                            <hr />
                            <label>Visit Orders</label>
                            <div class="w-50" v-if="visitOrders.length > 0">
                                <table class="table table-responsive-md border border-gray">
                                    <tbody>
                                        <tr class="bg-table-header">
                                            <th>Discipline</th>
                                            <th>Frequency</th>
                                            <th>Effective Date</th>
                                        </tr>
                                        <tr v-for="(order, index) of visitOrders" :key="index">
                                            <td>{{ order.discipline }}</td>
                                            <td class="white-space-pre">{{ order.frequency }}</td>
                                            <td class="white-space-pre">{{ order.effectiveDate }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p v-else>There are no current orders for visits.</p>
                        </div>
                        <div class="form-group col-12 col-md-6 mt-2">
                            <label for="repeat"> Repeat </label>
                            <select id="repeat" class="form-select" v-model="newTaskRepeat">
                                <option v-for="[key, value] in enumToMap(tasks)" :key="key" :value="value.value">
                                    {{ value.name }}
                                </option>
                            </select>
                        </div>

                        <div class="form-group col-12 col-md-6 mt-2">
                            <label for="date-to"> Date </label><span class="text-danger">*</span>
                            <i
                                class="fw-normal ms-1"
                                v-tooltip="{ title: `Current Benefit Period` }"
                                v-if="patient && !isPalliativeSolution"
                            >
                                <span
                                    v-if="
                                        (patient.benefitPeriodStartDate || patient.transferBenefitPeriodStartDate) &&
                                        patient.benefitPeriodEndDate
                                    "
                                >
                                    <span>(Current Benefit Period: </span>
                                    <strong v-if="patient.isTransfer">{{
                                        formatDate(patient.transferBenefitPeriodStartDate)
                                    }}</strong>
                                    <strong v-else>{{ formatDate(patient.benefitPeriodStartDate) }}</strong>
                                    <span class="mx-1">-</span>
                                    <strong>{{ formatDate(patient.benefitPeriodEndDate) }})</strong>
                                </span>
                                <span v-else>
                                    <strong v-if="isPatientPending">(Current Benefit Period: Pending)</strong>
                                    <span v-else> No Benefit Period Found </span>
                                </span>
                            </i>
                            <DatePicker :getDate="changeNewTaskSingleDate" v-if="newTaskRepeat == 0">
                                <div class="input-group date datepickerInput">
                                    <input
                                        name="newTaskSingleDate"
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(newTaskSingleDate)"
                                        @blur="v$.newTaskSingleDate?.$touch"
                                        :class="`${!v$.newTaskSingleDate?.$error ? '' : 'field-invalid mt-0'}`"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newTaskSingleDate?.$error" class="field-invalid">Date is required.</span>
                            </DatePicker>
                            <DateRangePicker
                                v-if="newTaskRepeat == 1 || newTaskRepeat == 2 || newTaskRepeat == 4"
                                :options="newTaskRangePickerOptions"
                            >
                                <input
                                    type="text"
                                    class="form-control"
                                    placeholder="MM/DD/YYYY"
                                    name="daterange"
                                    v-model="newTaskDateRange"
                                    @change="setNewTaskDateRange"
                                    @blur="v$.newTaskDateRange?.$touch"
                                    :class="`${!v$.newTaskDateRange?.$error ? '' : 'field-invalid'}`"
                                />
                                <span v-if="v$.newTaskDateRange?.$error" class="field-invalid">Date is required.</span>
                            </DateRangePicker>
                            <MultiDatePicker
                                v-if="newTaskRepeat == 3"
                                :result="newTaskFlexDates"
                                v-on:updateResult="updatenewTaskFlexDates"
                            >
                                <input
                                    class="form-control"
                                    placeholder="MM/DD/YYYY"
                                    @blur="v$.newTaskFlexDates?.$touch"
                                    :class="`${!v$.newTaskFlexDates?.$error ? '' : 'field-invalid mt-0'}`"
                                />
                            </MultiDatePicker>
                            <span v-if="v$.newTaskFlexDates?.$error" class="field-invalid">Date is required.</span>
                        </div>
                        <div class="form-group col-12 col-md-6 mt-2">
                            <label for="task"> Task </label><span class="text-danger">*</span>
                            <TypeaheadInput
                                :getData="availableTasksFilter"
                                :value="taskList"
                                v-on:valueChanged="changeTaskList"
                                placeholder="Type to Filter Task"
                                :disabled="!isDateSelected"
                                :show-all-by-default="true"
                                :open-on-focus="true"
                                :must-select-value="true"
                                ref="typeAheadRefTask"
                                v-on:blurredInput="v$.taskList?.$touch"
                                :class="`${!v$.taskList?.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.taskList?.$error" class="field-invalid">Task is required.</span>
                        </div>
                        <div class="form-group col-12 col-md-6 mt-2">
                            <label for="employee"> Employee </label>
                            <TypeaheadInput
                                :getData="employeeListFilter"
                                :value="scheduleEmployeeId"
                                v-on:valueChanged="changeScheduleEmployeeId"
                                placeholder="Type to Search Employee"
                                :show-all-by-default="true"
                                :disabled="!isDateSelected || !isTaskSelected || employeeIsPreSelected"
                                ref="typeAheadRefEmp"
                            />
                        </div>
                        <div class="form-group col-6 col-md-4 mt-2">
                            <label for="payer"> Payer </label><span class="text-danger">*</span>
                            <select
                                class="form-select"
                                name="payer"
                                v-model="newTask.patientPayerId"
                                @blur="v$.newTask.patientPayerId?.$touch"
                                :class="`${!v$.newTask.patientPayerId?.$error ? '' : 'field-invalid'}`"
                            >
                                <option :value="null">Select Payer</option>
                                <optgroup v-for="payer in payerTypes" :key="payer.name" :label="payer.name">
                                    <option
                                        v-for="type in patientPayers.filter((p) => p.payerType === payer.value)"
                                        :value="type.id"
                                        :key="type.id"
                                    >
                                        {{ type.name }}
                                    </option>
                                </optgroup>
                            </select>
                            <span v-if="v$.newTask.patientPayerId?.$error" class="field-invalid"
                                >Payer is required.</span
                            >
                        </div>
                        <div class="form-group col-6 col-md-4 mt-2" v-if="!isPalliativeSolution">
                            <label for="shift-length"> Shift Length </label>
                            <select class="form-select" v-model="shiftLength">
                                <option value="0">Select Shift Length</option>
                                <option v-for="i in 12" :key="i" :value="i">
                                    {{ i }} {{ i === 1 ? "Hour" : "Hours" }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-6 col-md-4 mt-2" v-if="!isPalliativeSolution">
                            <label for="shift-time"> Shift Start Time </label>
                            <TimePicker @update-props="changeStartTime">
                                <input
                                    type="text"
                                    class="form-control"
                                    :value="shiftStartTime"
                                    autocomplete="off"
                                    maxlength="8"
                                    :disabled="shiftLength <= 0"
                                    placeholder="Enter Shift Start Time"
                                />
                            </TimePicker>
                        </div>
                        <div class="form-group col-6 col-md-4 mt-2" v-else>
                            <label for="shift-time"> Scheduled Start Time </label>
                            <TimePicker @update-props="changeStartTime">
                                <input
                                    type="text"
                                    class="form-control"
                                    :value="shiftStartTimePal"
                                    autocomplete="off"
                                    maxlength="8"
                                    placeholder="Enter Scheduled Start Time"
                                />
                            </TimePicker>
                        </div>
                        <div class="form-group col-6 col-md-4 mt-2" v-if="isPalliativeSolution">
                            <label for="shift-end-time"> Scheduled End Time </label
                            ><span class="text-danger" v-if="shiftStartTimePal">*</span>
                            <TimePicker @update-props="changeEndTime">
                                <input
                                    type="text"
                                    class="form-control"
                                    :value="shiftEndTime"
                                    autocomplete="off"
                                    maxlength="8"
                                    :disabled="!shiftStartTimePal"
                                    placeholder="Enter Scheduled End Time"
                                    @blur="v$.shiftEndTime?.$touch"
                                    :class="`${!v$.shiftEndTime?.$error ? '' : 'field-invalid'}`"
                                />
                            </TimePicker>
                            <span v-if="v$.shiftEndTime?.$error" class="field-invalid"
                                >Shift End Time is required.</span
                            >
                        </div>
                        <div
                            class="form-group form-check custom-checkbox col-4 col-md-2 mt-2 ms-3"
                            v-if="!isPalliativeSolution"
                        >
                            <input
                                type="checkbox"
                                id="on-call-visit"
                                class="form-check-input"
                                v-model="newTask.onCall"
                            />
                            <label class="form-check-label" for="on-call-visit"> On-Call Visit </label>
                        </div>
                        <div class="col-6 col-md-4 mt-2 ms-md-3 row" v-if="isPalliativeSolution">
                            <div class="form-group form-check custom-radio col-12 col-md-6">
                                <input
                                    type="radio"
                                    id="on-call"
                                    name="on-call-prn"
                                    class="form-check-input"
                                    v-model="visitType"
                                    :value="visitTypeEnum.OnCall"
                                    @click="visitTypeRadioClick(visitTypeEnum.OnCall)"
                                />
                                <label class="form-check-label" for="on-call">
                                    <span class="me-1">On-Call Visit</span>
                                </label>
                            </div>
                            <div class="form-group form-check custom-radio col-12 col-md-6">
                                <input
                                    type="radio"
                                    id="prn"
                                    name="on-call-prn"
                                    class="form-check-input"
                                    v-model="visitType"
                                    :value="visitTypeEnum.Prn"
                                    @click="visitTypeRadioClick(visitTypeEnum.Prn)"
                                />
                                <label class="form-check-label" for="prn">
                                    <span class="me-1">PRN Visit</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <section
                        class="col-12 col-lg-10 offset-md-1 row g-0 mb-2"
                        v-if="newTaskRepeat > 0 && newTask.startDates.length > 0 && isPalliativeSolution"
                    >
                        <section class="col-12 col-lg-11 bg-white text-center selected-dates-wrap">
                            <b>{{ taskList?.name }}</b> task will be assigned to <b>{{ employeeName }}</b> on the
                            following dates:<br />
                            <span v-for="(item, index) in newTask.startDates" :key="item">
                                <b>{{ formatDate(item, "MMM DD, YYYY") }}</b>
                                <span class="me-1" v-if="index !== newTask.startDates.length - 1">,</span> </span
                            ><br />
                            <span v-if="shiftLength && shiftStartTime">
                                From <b>{{ shiftStartTime }}</b> to <b>{{ shiftEndTime }}</b>
                            </span>
                        </section>
                    </section>
                    <section class="col-12 col-lg-10 offset-md-1 row g-0 justify-content-center">
                        <div
                            class="col-12 col-lg-10 col-md-5 pe-2 ms-2"
                            v-if="taskListItems && taskListItems.id && !isPalliativeSolution"
                        >
                            <FrequencyCheck
                                :visit-details="visitDetails"
                                :visit-dates="newTaskStartDates"
                                v-on:updateVisitDates="updateVisitDates"
                                :selected-task="taskListItems"
                                :is-outside-frequency="isOutsideFrequency"
                                v-on:updateOutsideFrequency="updateOutsideFrequency"
                            />
                        </div>
                    </section>
                </section>
                <div class="d-flex flex-wrap-reverse flex-md-nowrap justify-content-center my-2" v-if="!isLoading">
                    <template v-if="!isOutsideFrequency">
                        <button type="button" class="btn btn-primary btn-md" @click="createTask('1')">Save Task</button>
                        <SaveNotifyUser
                            :agency-id="visitDetails.AgencyId"
                            button-name="Save and Notify Employee"
                            v-on:handleSaveVisit="createTask('3')"
                            info-classes="pt-2 mt-2"
                        />
                        <button type="button" class="btn btn-primary btn-md ms-2" @click="saveAndAddAnother('2')">
                            Save Task and Add Another
                        </button>
                    </template>
                    <template v-else>
                        <button type="button" class="btn btn-primary btn-md" @click="showFrequencyWarning('1')">
                            Save Task
                        </button>
                        <SaveNotifyUser
                            :agency-id="visitDetails.AgencyId"
                            button-name="Save and Notify Employee"
                            v-on:handleSaveVisit="showFrequencyWarning('3')"
                            info-classes="pt-2 mt-2"
                        />
                        <button type="button" class="btn btn-primary btn-md ms-2" @click="showFrequencyWarning('2')">
                            Save Task and Add Another
                        </button>
                    </template>
                    <span>
                        <button
                            type="button"
                            class="ms-2 mb-2 mb-md-0 btn btn-md btn-outline-secondary"
                            @click="cancel()"
                        >
                            Cancel
                        </button>
                    </span>
                </div>
            </template>
        </FormSection>
    </section>
    <section v-else class="p-3" v-show="!isPalliativeSolution && !isScheduling">
        <h6 class="text-center text-warning fw-bold">
            <i class="fas fa-exclamation-triangle"></i>
            <span> Warning: The task you are attempting to schedule is outside of the ordered visit frequency. </span>
        </h6>
        <h6 class="text-center text-warning fw-bold">Do you want to continue scheduling?</h6>
        <div class="text-center">
            <button type="button" class="btn btn-xs btn-primary" @click="createTask(saveType)">Schedule Task</button>
            <button type="button" class="btn btn-xs btn-outline-secondary ms-2" @click="closeFrequencyWarning()">
                Cancel
            </button>
        </div>
    </section>
    <section v-if="isScheduling">
        <section class="p-5 d-flex flex-column text-center text-primary">
            <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
            <strong class="mt-1">
                <b>Save Task In Progress...</b>
            </strong>
        </section>
    </section>
</template>
<style scoped lang="scss" src="./ScheduleVisitHosPal.scss"></style>
<script lang="ts" src="./ScheduleVisitHosPal.ts"></script>
